// Lanzar las 3 primeras piezas y esperarlas (ADR-2026-09-28-onboarding-v2-etapa-1).
// Van como un lote del agente: el backend chequea el saldo antes de encolar y cobra
// 1 crédito por imagen entregada. Cada ítem tiene su `execution_id`, así cada imagen
// que llega se sabe de qué pieza es.

import { atlasBackendFetch } from '../atlasBackend';
import { pollGeneratedByExecutionId } from '../../pages/v2/pollGeneratedByExecutionId';
import { preloadGeneratedImages } from '../../utils/preloadGeneratedImages';
import { FORMATO_DE_LAS_PIEZAS, type ItemDelLote, type PiezaId } from './piezas';
import { enGuion, piezasDelGuion } from './guion';
import { RESOLUCION_DEFAULT } from '../resolucionImagen';

export type LanzamientoDePiezas =
  | { ok: true; ejecuciones: Partial<Record<PiezaId, string>> }
  | { ok: false; error: string; sinCreditos: boolean };

export async function lanzarPiezas(items: ItemDelLote[]): Promise<LanzamientoDePiezas> {
  if (enGuion()) {
    return { ok: true, ejecuciones: Object.fromEntries(items.map((it) => [it.id, `guion-${it.id}`])) };
  }
  try {
    const res = await atlasBackendFetch('/v1/agent/lote', {
      items,
      formato: FORMATO_DE_LAS_PIEZAS,
      image_size: RESOLUCION_DEFAULT,
      titulo: 'Primeras piezas',
    });
    const json = (await res.json().catch(() => ({}))) as {
      items?: Array<{ id: string; execution_id: string }>;
      error?: string;
    };
    if (res.status === 402) {
      return {
        ok: false,
        sinCreditos: true,
        error: 'No te alcanzan los créditos para las 3 piezas. Agrega créditos o elige un plan para seguir.',
      };
    }
    if (!res.ok || !Array.isArray(json.items)) {
      return { ok: false, sinCreditos: false, error: 'No pudimos empezar a crear tus piezas. Intenta de nuevo.' };
    }
    const ejecuciones: Partial<Record<PiezaId, string>> = {};
    for (const it of json.items) ejecuciones[it.id as PiezaId] = it.execution_id;
    return { ok: true, ejecuciones };
  } catch {
    return { ok: false, sinCreditos: false, error: 'No pudimos conectarnos. Revisa tu conexión e intenta de nuevo.' };
  }
}

export interface PiezaLista {
  id: PiezaId;
  imagenId: string;
  url: string;
}

/**
 * Espera las piezas y avisa cada una apenas está (con su URL firmada lista para
 * mostrar). Las que fallan se avisan con `onFallo`. Resuelve cuando llegaron todas o
 * cuando el poll corta por tiempo, y SIEMPRE después de avisar cada una: una fila que el
 * progreso empezó a precargar queda en `vistas`, así que el último `atender` la saltea, y
 * sin esperar esa precarga quien llama veía el lote sin piezas (review Greptile #164).
 */
export async function esperarPiezas(p: {
  ejecuciones: Partial<Record<PiezaId, string>>;
  companyId: string;
  onPieza: (pieza: PiezaLista) => void;
  onFallo: (id: PiezaId) => void;
  signal?: AbortSignal;
}): Promise<void> {
  if (enGuion()) return piezasDelGuion(p.onPieza);
  const piezaPorEjecucion = new Map<string, PiezaId>();
  for (const [id, ejecucion] of Object.entries(p.ejecuciones)) {
    if (ejecucion) piezaPorEjecucion.set(ejecucion, id as PiezaId);
  }
  const vistas = new Set<string>();

  const atender = async (rows: Array<{ id: string; has_error: boolean; execution_id?: string | null }>) => {
    for (const row of rows) {
      if (vistas.has(row.id)) continue;
      vistas.add(row.id);
      const pieza = row.execution_id ? piezaPorEjecucion.get(row.execution_id) : undefined;
      if (!pieza) continue;
      if (row.has_error) {
        p.onFallo(pieza);
        continue;
      }
      const img = await preloadGeneratedImages([row.id], p.companyId)
        .then(([primera]) => primera)
        .catch(() => undefined);
      if (img?.signedUrl) p.onPieza({ id: pieza, imagenId: String(row.id), url: img.signedUrl });
      else p.onFallo(pieza);
    }
  };

  const enCurso: Promise<void>[] = [];
  const filas = await pollGeneratedByExecutionId({
    executionId: [...piezaPorEjecucion.keys()],
    companyId: p.companyId,
    expectedCount: piezaPorEjecucion.size,
    signal: p.signal,
    onProgreso: (rows) => {
      enCurso.push(atender(rows));
    },
  });
  await atender(filas);
  await Promise.all(enCurso);
}
